import { Link } from "react-router-dom";
import { TopBar } from "../components/Layout";

type Status = "done" | "pending" | "blocking";

interface Item {
  title: string;
  detail: string;
  where?: string;
  status: Status;
}

const SECRETS: Item[] = [
  { title: "CF_ACCOUNT_ID", detail: "Cloudflare account id for the D1 HTTP API.", where: "all 7 workers", status: "done" },
  { title: "DIRECTORY_KEK", detail: "Envelope-encryption key for tenant Entra secrets.", where: "ac-edge", status: "done" },
  { title: "ADMIN_API_TOKEN", detail: "Sign-in token for this console.", where: "ac-admin", status: "done" },
  {
    title: "CF_API_TOKEN",
    detail:
      "Cloudflare API token (scopes: D1 write, Workers KV write, DNS edit). THE blocker for tenant runtime " +
      "and provisioning — until it's set, the edge can't reach per-tenant D1, so tenant/portal login won't work.",
    where: "ac-edge, ac-portal, ac-email-in, ac-email-out, ac-notifications, ac-provisioner",
    status: "blocking",
  },
  { title: "CF_ZONE_ID", detail: "actionconnect.com zone id — for per-tenant subdomains/DNS at provisioning.", where: "ac-provisioner", status: "pending" },
  { title: "CF_KV_NAMESPACE_ID", detail: "ac-routing KV id (eda6228c…) the provisioner writes host records into.", where: "ac-provisioner", status: "pending" },
  { title: "MAILGUN_API_KEY + MAILGUN_DOMAIN", detail: "Outbound email — notifications, replies, magic-link & contract emails.", where: "ac-email-out", status: "pending" },
];

const DECISIONS: Item[] = [
  { title: "Pick the logo", detail: "Choose one of the 5 concepts (recommended: Linked Chevrons) so it can be wired in as the favicon/brand across surfaces.", status: "pending" },
  { title: "Go-live domain", detail: "Staging is workers.dev with no production routes. Decide when to wire actionconnect.com routes/DNS to go live on the real domain.", status: "pending" },
  { title: "Staff admin SSO", detail: "Pick the staff IdP (Google Workspace / Entra / WorkOS) to replace the placeholder ADMIN_API_TOKEN gate on this console.", status: "pending" },
  { title: "Tenant scale / sharding", detail: "Confirm the target tenant count so we know when D1 per-account limits require multi-account sharding (the registry already supports it).", status: "pending" },
  { title: "Inbound email domain", detail: "Confirm in.actionconnect.com Email Routing so {slug}@in.actionconnect.com lands as tickets.", status: "pending" },
];

const SECURITY: Item[] = [
  { title: "Rotate committed credentials", detail: "ADMIN_API_TOKEN and the POC password are documented in committed source. If the GitHub repo is public, rotate both after the push.", status: "blocking" },
  { title: "Confirm repo visibility", detail: "Keep github.com/wpolancozentrosoft/ActionConnect private (it carries the architecture and the above credentials).", status: "pending" },
];

function badge(s: Status) {
  if (s === "done") return <span className="badge status-active">done</span>;
  if (s === "blocking") return <span className="badge status-failed">action needed</span>;
  return <span className="badge status-suspended">pending</span>;
}

function ItemTable({ rows }: { rows: Item[] }) {
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Item</th>
          <th>Detail</th>
          <th>Where</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.title}>
            <td><strong>{r.title}</strong></td>
            <td style={{ fontSize: "0.84rem" }}>{r.detail}</td>
            <td className="mono" style={{ fontSize: "0.74rem" }}>{r.where ?? "—"}</td>
            <td>{badge(r.status)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ResourcePendingPage() {
  return (
    <>
      <TopBar crumbs={<><Link to="/resources">Resources</Link> / Action items (from you)</>} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Action items (from you)</h1>
            <div className="sub">
              What&apos;s still needed to take ActionConnect from this staging deploy to production — secrets,
              decisions, and housekeeping. Updated as items land.
            </div>
          </div>
        </div>

        <div className="alert info">
          The platform code is built, tested, and deployed to staging; the POC tenant and this admin console are
          live. The items below are the things only you can provide or decide.
        </div>

        <div className="card">
          <div className="card-head"><h2>1 · Secrets to set</h2></div>
          <div className="card-body tight"><ItemTable rows={SECRETS} /></div>
        </div>

        <div className="card">
          <div className="card-head"><h2>2 · Decisions</h2></div>
          <div className="card-body tight"><ItemTable rows={DECISIONS} /></div>
        </div>

        <div className="card">
          <div className="card-head"><h2>3 · Security &amp; housekeeping</h2></div>
          <div className="card-body tight"><ItemTable rows={SECURITY} /></div>
        </div>

        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Set secrets with <code>wrangler secret put &lt;NAME&gt; --env=&quot;&quot;</code> from each worker dir —
          see <Link to="/resources/api-keys">API keys &amp; vendors</Link>. Environment URLs and the POC login are
          on <Link to="/resources/staging">Staging &amp; POC tenant</Link>.
        </p>
      </div>
    </>
  );
}
